/* eslint-disable */

/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {fetch} from 'coreUtils/utils';
import {keysToCamel} from './utils';

const PRODUCTION_INSTANCE = 'prd';

class EinsteinAPI {
	constructor(commerceAPI) {
		this.commerceAPI = commerceAPI;
		this.config = commerceAPI?._config?.einsteinConfig;
	}

	/**
     * Given a product or item source, returns the product data that Einstein requires
     */
    _constructEinsteinProduct(product) {
        if (product.type && (product.type.master || product.type.variant)) {
            // handle variants for PDP / viewProduct
            // Assumes product is a Product object from SCAPI Shopper-Products:
            // https://developer.salesforce.com/docs/commerce/commerce-api/references/shopper-products?meta=type%3AProduct
            return {
                id: product.master.masterId,
                sku: product.id,
                altId: '',
                altIdType: ''
            }
		} else if (product?.type?.variationGroup) {
			return {
				id: product.master.masterId,
				sku: product.id,
				altId: product.id,
				altIdType: 'vgroup'
			};
		} else if (
            product.productType &&
            (product.productType.master ||
                product.productType.variant ||
                product.productType.set ||
                product.productType.bundle ||
                product.productType.variationGroup ||
                product.productType.item)
        ) {
            // handle variants & sets for PLP / viewCategory & viewSearch
            // Assumes product is a ProductSearchHit from SCAPI Shopper-Search:
            // https://developer.salesforce.com/docs/commerce/commerce-api/references/shopper-search?meta=type%3AProductSearchHit
            return {
                id: product.productId,
                sku: product.productId, //TODO: Should we switch this to product.representedProduct.id once we allow non-master products in search results?
                altId: '',
                altIdType: ''
            }
        } else {
            // handles non-variants
            return {
                id: product.id,
                sku: '',
                altId: '',
                altIdType: ''
            }
        }
    }

	/**
     * Given a cart item, returns the data that Einstein requires
     *
     * Assumes item is a ProductItemfrom SCAPI Shopper-Baskets:
     * https://developer.salesforce.com/docs/commerce/commerce-api/references/shopper-baskets?meta=type%3AProductItem
     */
    _constructEinsteinItem(item) {
        return {
            id: item.productId,
            sku: '',
            price: item.price,
            quantity: item.quantity
        }
    }

	/**
	 * Given a POJO append the correct user and cookie identifier values using the current auth state.
	 *
	 * @param {object} params
	 * @returns {object} The decorated body object.
	 */
	_buildBody(params) {
		const body = {...params};
		let cookieId = this.commerceAPI.auth.getFirstCqcid();

		if (!cookieId && this.commerceAPI.auth.dwanonymous) {
			this.commerceAPI.auth.setFirstCqcid(this.commerceAPI.auth.dwanonymous);
			cookieId = this.commerceAPI.auth.dwanonymous;
		}

		// The unique identifier of an anonymous shopper. If you're making API requests from a traditional Commerce Cloud storefront,
		// pass the dwanonymous_* cookie value (See Browser-Based Local Data Storage for more information).
		// https://documentation.b2c.commercecloud.salesforce.com/DOC1/index.jsp?topic=/com.demandware.dochelp/DataProtectionAndPrivacy/LocalDataStorage.html
		if (cookieId) {
			body.cookieId = cookieId;
		} else {
			console.warn('Missing `cookieId`. For optimal results this value must be defined.');
		}

		// The first part of the siteId is the realm
		if (this.config.siteId) {
			body.realm = this.config.siteId.split('-')[0];
		}

		body.instanceType = PRODUCTION_INSTANCE;

		return body;
	}

	async einsteinFetch(endpoint, method, body) {
		const config = this.config;
		const {host, einsteinId} = config
		const headers = {
			'Content-Type': 'application/json',
			'x-cq-client-id': einsteinId
		};

		// Include `userId` and `cookieId` parameters.
		if (body) {
			body = this._buildBody(body);
		}

		let response;
		response = await fetch(`${host}/v3${endpoint}`, {
			method: method,
			headers: headers,
			...(body && {
				body: JSON.stringify(body)
			})
		});

		const responseJson = await response.json();

		return keysToCamel(responseJson);
	}

	/**
	 * Tells the Einstein engine when a user views a product.
	 * https://developer.salesforce.com/docs/commerce/einstein-api/references#einstein-recommendations:Summary
	 **/
	async sendViewProduct(product, args) {
		const endpoint = `/activities/${this.config.siteId}/viewProduct`;
		const method = 'POST';
		const body = {
			product: this._constructEinsteinProduct(product),
			...args
		};

		return this.einsteinFetch(endpoint, method, body);
	}

	/**
  * Tells the Einstein engine when a user views search results.
  **/
	async sendViewSearch(searchText, searchResults, args) {
		const endpoint = `/activities/${this.config.siteId}/viewSearch`;
		const method = 'POST';

		const products = searchResults?.hits?.map((product) =>
			this._constructEinsteinProduct(product)
		);

		const body = {
			searchText,
			products,
			showProducts: !products || !products.length ? false : true,
			...args
		};

		return this.einsteinFetch(endpoint, method, body);
	}

	/**
	 * Tells the Einstein engine when a user clicks on a search result.
	 **/
	async sendClickSearch(searchText, product, args) {
		const endpoint = `/activities/${this.config.siteId}/clickSearch`;
		const method = 'POST';
		const body = {
			searchText,
			product: this._constructEinsteinProduct(product),
			...args
		};

		return this.einsteinFetch(endpoint, method, body);
	}

	/**
	 * Tells the Einstein engine when a user views a category.
	 **/
	async sendViewCategory(category, searchResults, args) {
		const endpoint = `/activities/${this.config.siteId}/viewCategory`;
		const method = 'POST';

		const products = searchResults?.hits?.map((product) =>
			this._constructEinsteinProduct(product)
		);

		const body = {
			category: {
				id: category.id
			},
			products,
			showProducts: true,
			...args
		};

		return this.einsteinFetch(endpoint, method, body);
	}

	/**
	 * Tells the Einstein engine when a user clicks a product from the category page.
	 * Not meant to be used when the user clicks a category from the nav bar.
	 **/
	async sendClickCategory(category, product, args) {
		const endpoint = `/activities/${this.config.siteId}/clickCategory`;
		const method = 'POST';
		const body = {
			category: {
				id: category.id
			},
			product: this._constructEinsteinProduct(product),
			...args
		};

		return this.einsteinFetch(endpoint, method, body);
	}

	/**
	 * Tells the Einstein engine when a user views a set of recommendations
	 * https://developer.salesforce.com/docs/commerce/einstein-api/references#einstein-recommendations:Summary
	 **/
	async sendViewReco(recommenderDetails, args) {
		const endpoint = `/activities/${this.config.siteId}/viewReco`;
		const method = 'POST';
		const body = {
			...recommenderDetails,
			...args
		};

		return this.einsteinFetch(endpoint, method, body);
	}

	/**
	 * Tells the Einstein engine when a user clicks on a recommendation
	 * https://developer.salesforce.com/docs/commerce/einstein-api/references#einstein-recommendations:Summary
	 **/
	async sendClickReco(recommenderDetails, product, args) {
		const endpoint = `/activities/${this.config.siteId}/clickReco`;
		const method = 'POST';
		const {__recoUUID, recommenderName} = recommenderDetails;
		const body = {
			recommenderName,
			__recoUUID,
			product: this._constructEinsteinProduct(product),
			...args
		};

		return this.einsteinFetch(endpoint, method, body);
	}

	/**
		 * Tells the Einstein engine when a user views a page.
		 * Use this only for pages where another activity does not fit. (ie. on the PDP, use viewProduct rather than this)
		 **/
	async sendViewPage(path, args) {
		const endpoint = `/activities/${this.config.siteId}/viewPage`;
		const method = 'POST';
		const body = {
			currentLocation: path,
			...args
		};

		return this.einsteinFetch(endpoint, method, body);
	}

	/**
	 * Tells the Einstein engine when a user starts the checkout process.
	 **/
	async sendBeginCheckout(basket, args) {
		const endpoint = `/activities/${this.config.siteId}/beginCheckout`;
		const method = 'POST';
		const products = basket.productItems.map((product) => {
			const {productId, sku = '', price = '', quantity = ''} = product;

			return {
				id: productId,
				sku,
				price,
				quantity
			};
		});
		const subTotal = basket.productSubTotal;
		const body = {
			products: products,
			amount: subTotal,
			...args
		};

		return this.einsteinFetch(endpoint, method, body);
	}

	/**
	 * Tells the Einstein engine when a user reaches the given step during checkout.
	 * https://developer.salesforce.com/docs/commerce/einstein-api/references#einstein-recommendations:Summary
	 **/
	async sendCheckoutStep(stepName, stepNumber, basket, args) {
		const endpoint = `/activities/${this.config.siteId}/checkoutStep`;
		const method = 'POST';
		const body = {
			stepName,
			stepNumber,
			basketId: basket.basketId,
			...args
		};

		return this.einsteinFetch(endpoint, method, body);
	}

	/**
	 * Tells the Einstein engine when a user adds an item to their cart.
	 * https://developer.salesforce.com/docs/commerce/einstein-api/references#einstein-recommendations:Summary
	 **/
	async sendAddToCart(product, args) {
		const endpoint = `/activities/${this.config.siteId}/addToCart`;
		const method = 'POST';
		const body = {
			products: [this._constructEinsteinItem(product)],
			...args
		};

		return this.einsteinFetch(endpoint, method, body);
	}

	/**
	 * Get a list of recommenders that can be used in recommendation requests.
	 * https://developer.salesforce.com/docs/commerce/einstein-api/references#einstein-recommendations:Summary
	 **/
	async getRecommenders() {
		const endpoint = `/personalization/recommenders/${this.config.siteId}`;
		const method = 'GET';
		const body = null;

		return this.einsteinFetch(endpoint, method, body);
	}

	/**
	 * Get a set of recommendations
	 * https://developer.salesforce.com/docs/commerce/einstein-api/references#einstein-recommendations:Summary
	 **/
	async getRecommendations(recommenderName, args) {
		const endpoint = `/personalization/recs/${this.config.siteId}/${recommenderName}`;
		const method = 'POST';
		const body = {...args};
		// Fetch the recommendations
		const reco = await this.einsteinFetch(endpoint, method, body);

		reco.recommenderName = recommenderName;

		return reco;
	}

	/**
	 * Get a set of recommendations for a zone
	 * https://developer.salesforce.com/docs/commerce/einstein-api/references#einstein-recommendations:Summary
	 **/
	async getZoneRecommendations(zoneName, args) {
		const endpoint = `/personalization/${this.config.siteId}/zones/${zoneName}/recs`;
		const method = 'POST';
		const body = {...args};
		// Fetch the recommendations
		const reco = await this.einsteinFetch(endpoint, method, body);

		return reco;
	}

	/**
	 * Sets the Einstein site ID in the configuration.
	 * @param {string|undefined} siteId - The Einstein site ID to set.
	 */
	setEinsteinSiteId(siteId) {
		if (siteId) {
			this.config.siteId = siteId;
		}
	}

	/**
	 * Sets the Einstein client ID in the configuration.
	 * @param {string|undefined} einsteinId - The Einstein client ID to set.
	 */
	setEinsteinClientId(einsteinId) {
		if (einsteinId) {
			this.config.einsteinId = einsteinId;
		}
	}
}

export default EinsteinAPI;
